import { associateDestroyableChild, registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import type FlashMessagesService from '../services/flash-messages-service.ts';
import type { FlashMessageOptions } from '../services/flash-messages-service.ts';

export type FlashMessageState = 'open' | 'closed';

export default class FlashMessageObject {
  private flashMessagesService: FlashMessagesService;

  @tracked message: string;
  readonly type: string;
  readonly duration: number;
  readonly sticky: boolean;
  readonly name: string;
  readonly onClose?: () => void;

  constructor(flashMessagesService: FlashMessagesService, options: FlashMessageOptions) {
    this.flashMessagesService = flashMessagesService;
    this.message = options.message;
    this.type = options.type;
    this.sticky = options.sticky ?? false;
    const duration =
      typeof options.duration === 'boolean'
        ? options.duration === true
          ? 10000
          : 0
        : (options.duration ?? 0);
    this.duration = this.sticky ? 0 : duration;
    this.name = options.name ?? '';
    this.onClose = options.onClose;

    associateDestroyableChild(this.flashMessagesService, this);
    registerDestructor(this, this.remove);

    if (this.duration > 0) {
      void this.durationTimerTaskInstance.perform(this.duration);
    }
  }

  private durationTimerTaskInstance = task(async (delay: number) => {
    await timeout(delay);
    this.close();
  });

  private closeTimerTaskInstance = task(async () => {
    await timeout(500);
    this.remove();
  });

  @tracked state: FlashMessageState = 'open';

  /** Starts te progress of removing the `FlashMessage` so we can animate */
  @action close(): void {
    this.state = 'closed';
    // keep the delay in sync with the close keyframe in the css file
    void this.closeTimerTaskInstance.perform();
  }

  /** Effectively remove the `FlasMessage` and cleanup everything */
  @action remove(): void {
    const queue = this.flashMessagesService.queue;
    const index = queue.indexOf(this);
    if (index !== -1) {
      queue.splice(index, 1);
    }

    void this.durationTimerTaskInstance.cancelAll();

    void this.closeTimerTaskInstance.cancelAll();

    this.onClose?.();
  }
}
